import { combineActions, handleActions } from 'redux-actions';
import {
    DELETE_BANK_ACCOUNT_FAIL,
    DELETE_BANK_ACCOUNT_REQUEST,
    DELETE_BANK_ACCOUNT_SUCCESS,
    DELETE_BANK_ACCOUNT_SUCCESS_ACTION,
    LOAD_BANK_ACCOUNTS_FAIL,
    LOAD_BANK_ACCOUNTS_REQUEST,
    LOAD_BANK_ACCOUNTS_SUCCESS,
    LOAD_BANK_ACCOUNTS_SUCCESS_ACTION,
    POST_BANK_ACCOUNT_FAIL,
    POST_BANK_ACCOUNT_REQUEST,
    POST_BANK_ACCOUNT_SUCCESS,
    POST_BANK_ACCOUNT_SUCCESS_ACTION,
} from './actions';
import { deleteBankAccount, fetchBankAccounts, postAddBankAccount } from '@/redux/api/payment';
import { getAuthToken, getBidderId } from './user';
import { ActionWithPayload } from '@/types/redux';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import paymentProviders from '../../enums/paymentProviders';

const DEFAULT_STATE = {
    bankAccounts: [],
    bankAccountsLoaded: undefined,
    bankAccountsLoading: false,
    errorMessage: null,
};

export type BankAccountState = {
    bankAccounts: BankAccount[];
    bankAccountsLoaded?: Date;
    bankAccountsLoading: boolean;
    error: any;
};

export const bankAccount = handleActions(
    {
        [combineActions(LOAD_BANK_ACCOUNTS_FAIL, POST_BANK_ACCOUNT_FAIL, DELETE_BANK_ACCOUNT_FAIL)]: (
            state: BankAccountState,
            action: ActionWithPayload<any>
        ) => ({
            ...state,
            bankAccountsLoading: false,
            errorMessage: action.payload,
        }),
        [combineActions(LOAD_BANK_ACCOUNTS_REQUEST, DELETE_BANK_ACCOUNT_REQUEST)]: (state: BankAccountState) => {
            return {
                ...state,
                bankAccountsLoaded: undefined,
                bankAccountsLoading: true,
                errorMessage: null,
            };
        },
        [DELETE_BANK_ACCOUNT_SUCCESS]: (state: BankAccountState, action: DELETE_BANK_ACCOUNT_SUCCESS_ACTION) => {
            return {
                ...state,
                bankAccounts: action.payload,
                errorMessage: null,
            };
        },
        [LOAD_BANK_ACCOUNTS_SUCCESS]: (state: BankAccountState, action: LOAD_BANK_ACCOUNTS_SUCCESS_ACTION) => {
            return {
                ...state,
                bankAccounts: action.payload || [],
                bankAccountsLoaded: new Date(),
                bankAccountsLoading: false,
                errorMessage: null,
            };
        },
        [POST_BANK_ACCOUNT_SUCCESS]: (state: BankAccountState, action: POST_BANK_ACCOUNT_SUCCESS_ACTION) => {
            return {
                ...state,
                bankAccounts: [...state.bankAccounts, action.payload],
                errorMessage: null,
            };
        },
    },
    DEFAULT_STATE
);

const stateSelector = (state: GlobalState) => state.bankAccount;

export const getBankAccounts = createSelector(stateSelector, (state) => state.bankAccounts);

export const getUiBankAccount: (state: any) => any = createSelector(stateSelector, (state) => state);

export const fetchBankAccountsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    //TODO if needed logic
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: LOAD_BANK_ACCOUNTS_REQUEST,
        });
        const response = await fetchBankAccounts({ authToken, bidderId, deployment });
        return dispatch({
            payload: response.payload,
            type: LOAD_BANK_ACCOUNTS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_BANK_ACCOUNTS_FAIL,
        });
    }
};

type SubmitPostBankAccountParams = {
    accountNumber: string;
    bankAccountName: string;
    firstName: string;
    lastName: string;
    providerId: number;
    routingNumber: string;
};

export const submitPostBankAccount = ({
    accountNumber,
    bankAccountName,
    firstName,
    lastName,
    routingNumber,
}: SubmitPostBankAccountParams) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: POST_BANK_ACCOUNT_REQUEST,
        });
        const response = await postAddBankAccount({
            accountNumber,
            authToken,
            bankAccountName,
            bidderId,
            deployment,
            firstName,
            lastName,
            providerId: paymentProviders.PAYRIX,
            routingNumber,
        });
        dispatch({
            payload: response.payload,
            type: POST_BANK_ACCOUNT_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: POST_BANK_ACCOUNT_FAIL,
        });
        throw error;
    }
};

export const submitDeleteBankAccount = (bankAccountId: string) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: DELETE_BANK_ACCOUNT_REQUEST,
        });

        await deleteBankAccount({ authToken, bankAccountId, bidderId, deployment });

        dispatch({ type: DELETE_BANK_ACCOUNT_SUCCESS });

        dispatch(fetchBankAccountsIfNeeded());
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: DELETE_BANK_ACCOUNT_FAIL,
        });
    }
};
